import React, { useState, useEffect } from "react";
import { getToken } from "../../utils/tokenUtils";
import {
  User,
  DollarSign,
  X,
  TrendingDown,
  TrendingUp,
  Calendar,
  Eye,
  Download,
  PieChartIcon,
  BarChart3,
  Calculator,
  FileText,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FaRupeeSign } from "react-icons/fa";

const MySalaryStructure = () => {
  const [salaryStructure, setSalaryStructure] = useState(null);
  const [structureHistory, setStructureHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const token = getToken();

  useEffect(() => {
    fetchSalaryStructure();
    fetchStructureHistory();
  }, []);

  const fetchSalaryStructure = async () => {
    try {
      setLoading(true);
      const employeeId = "EMP001"; // Mock employee ID - get from auth context in real app
      const response = await fetch(
        `https://hrmsnew.prepstripe.com/api/payroll/salary-structures/${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();

      setSalaryStructure(data.data);
    } catch (error) {
      console.error("Error fetching salary structure:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStructureHistory = async () => {
    try {
      const employeeId = "EMP005";
      const response = await fetch(
        `https://hrmsnew.prepstripe.com/api/payroll/salary-structures/${employeeId}/history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();

      setStructureHistory(data.data.data || []);
    } catch (error) {
      console.error("Error fetching structure history:", error);
    }
  };

  // Prepare chart data
  const earningsData =
    salaryStructure?.earnings_components?.map((component, index) => ({
      name: component.name,
      value: component.amount,
      color: ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444"][index % 5],
    })) || [];

  const deductionsData =
    salaryStructure?.deduction_components?.map((component, index) => ({
      name: component.name,
      value: component.amount,
      color: ["#ef4444", "#f97316", "#eab308", "#84cc16"][index % 4],
    })) || [];

  const salaryBreakdown = [
    {
      category: "Basic Salary",
      amount: salaryStructure?.basic_salary || 0,
      color: "#3b82f6",
    },
    {
      category: "Allowances",
      amount:
        (salaryStructure?.gross_salary || 0) -
        (salaryStructure?.basic_salary || 0),
      color: "#10b981",
    },
    {
      category: "Deductions",
      amount:
        salaryStructure?.deduction_components?.reduce(
          (sum, comp) => sum + (comp.amount || 0),
          0,
        ) || 0,
      color: "#ef4444",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto animate-pulse">
          <div className="bg-white rounded-lg h-64 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg h-96"></div>
            <div className="bg-white rounded-lg h-96"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!salaryStructure) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Salary Structure Found
            </h3>
            <p className="text-gray-600">
              Your salary structure has not been configured yet. Please contact
              HR.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="flex flex-col gap-2">
        {/* header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              My Salary Structure
            </h1>
            <p className="text-gray-600 mt-2 text-sm sm:text-base">
              Detailed breakdown of your salary components and benefits
            </p>
          </div>
          <button
            onClick={() => setShowHistoryModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md font-medium shadow-md flex items-center justify-center hover:bg-blue-700 w-full sm:w-auto"
          >
            <Calendar className="h-4 w-4 mr-2" />
            View History
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-1">
          <div className="bg-white text-gray-900 rounded-md shadow-md p-3">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <p className="text-sm text-gray-600">Annual CTC</p>
                <p className="text-lg font-semibold">
                  ₹{salaryStructure.ctc?.toLocaleString()}
                </p>
              </div>
              <TrendingUp className="h-6 w-6 text-gray-700" />
            </div>
          </div>

          <div className="bg-white text-gray-900 rounded-md shadow-md p-3">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <p className="text-sm text-gray-600">Monthly Gross</p>
                <p className="text-lg font-semibold">
                  ₹{salaryStructure.gross_salary?.toLocaleString()}
                </p>
              </div>
              <FaRupeeSign className="h-6 w-6 text-gray-700" />
            </div>
          </div>

          <div className="bg-white text-gray-900 rounded-md shadow-md p-3">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <p className="text-sm text-gray-600">Basic Salary</p>
                <p className="text-md font-semibold">
                  ₹{salaryStructure.basic_salary?.toLocaleString()}
                </p>
              </div>
              <Calculator className="h-6 w-6 text-gray-700" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {/* Earnings Components */}
          <div className="bg-white rounded-md shadow-md p-3">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-green-500" />
              Earnings Components
            </h3>

            {earningsData.length > 0 ? (
              <>
                {/* <div className="mb-6">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={earningsData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {earningsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div> */}

                <div className="space-y-3">
                  {salaryStructure.earnings_components.map(
                    (component, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-md shadow-md "
                      >
                        <div className="flex items-center">
                          <div
                            className="w-4 h-4 rounded-full mr-3"
                            style={{
                              backgroundColor: earningsData[index]?.color,
                            }}
                          ></div>
                          <div>
                            <p className="font-medium text-green-900">
                              {component.name}
                            </p>
                            {component.is_taxable && (
                              <p className="text-xs text-green-700">Taxable</p>
                            )}
                          </div>
                        </div>
                        <span className="text-md font-bold text-green-800">
                          ₹{component.amount?.toLocaleString()}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No earnings components configured</p>
              </div>
            )}
          </div>

          {/* Deductions Components */}
          <div className="bg-white rounded-md shadow-md p-3">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingDown className="h-5 w-5 mr-2 text-red-500" />
              Deduction Components
            </h3>

            {salaryStructure.deduction_components &&
            salaryStructure.deduction_components.length > 0 ? (
              <>
                {/* <div className="mb-6">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={deductionsData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {deductionsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div> */}

                <div className="space-y-3">
                  {salaryStructure.deduction_components.map(
                    (component, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-md shadow-md "
                      >
                        <div className="flex items-center">
                          <div
                            className="w-4 h-4 rounded-full mr-3"
                            style={{
                              backgroundColor: deductionsData[index]?.color,
                            }}
                          ></div>
                          <p className="font-medium text-md text-red-900">
                            {component.name}
                          </p>
                        </div>
                        <span className="text-md font-bold text-red-800">
                          ₹{component.amount?.toLocaleString()}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calculator className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No deductions configured</p>
              </div>
            )}
          </div>
        </div>

        {/* Salary Breakdown Chart */}
        {/* <div className=" bg-white rounded-md shadow-md p-3">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Monthly Salary Breakdown
          </h3>

          <div className="overflow-auto">
            <div className="min-w-[500px]"> 
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={salaryBreakdown} margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis
                    width={80}
                    tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                  />
                  <Tooltip
                    formatter={(value) => [`₹${value.toLocaleString()}`, "Amount"]}
                  />
                  <Bar dataKey="amount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div> */}

        {/* Structure Details */}
        <div className=" bg-white rounded-md shadow-md p-3">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Structure Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-3">
                Basic Information
              </h4>
              <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Employee ID:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {salaryStructure.employee_id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Effective From:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {new Date(
                      salaryStructure.effective_from,
                    ).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Last Updated:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {new Date(salaryStructure.updated_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className="text-sm font-medium text-green-600">
                    Active
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-3">
                Salary Calculation
              </h4>
              <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Annual CTC:</span>
                  <span className="text-sm font-medium text-gray-900">
                    ₹{salaryStructure.ctc?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Monthly Gross:</span>
                  <span className="text-sm font-medium text-gray-900">
                    ₹{salaryStructure.gross_salary?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    Estimated Deductions:
                  </span>
                  <span className="text-sm font-medium text-red-600">
                    ₹
                    {salaryStructure.deduction_components
                      ?.reduce((sum, comp) => sum + (comp.amount || 0), 0)
                      .toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between border-t border-gray-300 pt-2">
                  <span className="text-sm font-semibold text-gray-900">
                    Estimated Net:
                  </span>
                  <span className="text-sm font-bold text-green-600">
                    ₹
                    {(
                      (salaryStructure.gross_salary || 0) -
                      (salaryStructure.deduction_components?.reduce(
                        (sum, comp) => sum + (comp.amount || 0),
                        0,
                      ) || 0)
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Variable Components */}
        {/* {salaryStructure.variable_components && salaryStructure.variable_components.length > 0 && (
          <div className=" bg-white rounded-md shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-purple-500" />
              Variable Components
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {salaryStructure.variable_components.map((component, index) => (
                <div key={index} className="p-4 rounded-md shadow-md bg-purple-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-purple-900">{component.name}</p>
                      <p className="text-sm text-purple-700">Variable amount</p>
                    </div>
                    <span className="text-lg font-bold text-purple-800">₹{component.amount?.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded-md">
              <p className="text-sm text-yellow-800">
                💡 Variable components may change based on performance, attendance, or other factors.
              </p>
            </div>
          </div>
        )} */}

        {/* Salary History Modal */}
        {showHistoryModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-3 border w-11/12 md:w-4/5 shadow-lg rounded-md bg-white">
              <div className="mt-1">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Salary Structure History
                  </h3>
                  {/* <button
                    onClick={() => setShowHistoryModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button> */}

                  <button
                    type="button"
                    onClick={() => {
                      setShowHistoryModal(false);
                    }}
                    className="group relative p-2 rounded-xl  transition-all duration-200 hover:scale-105 active:scale-95"
                    aria-label="Close modal"
                  >
                    <X
                      size={20}
                      className="text-gray-600 transition-colors duration-200"
                    />
                    <div className="absolute inset-0 rounded-xl ransition-all duration-200"></div>
                  </button>
                </div>

                <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
                  <table className="min-w-full table-auto md:table-fixed text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
                      <tr>
                        <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-gray-black ">
                          Effective Date
                        </th>
                        <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-gray-black ">
                          Basic Salary
                        </th>
                        <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-gray-black ">
                          Gross Salary
                        </th>
                        <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-gray-black ">
                          CTC
                        </th>
                        <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-gray-black ">
                          Change
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {structureHistory.length === 0 ? (
                        <tr>
                          <td
                            colSpan="5"
                            className="px-6 py-4 text-center text-gray-500"
                          >
                            No history available
                          </td>
                        </tr>
                      ) : (
                        structureHistory.map((history, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(
                                history.effective_from,
                              ).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ₹{history.basic_salary?.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ₹{history.gross_salary?.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ₹{history.ctc?.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {index < structureHistory.length - 1 && (
                                <span
                                  className={`px-2 py-1 rounded-full text-xs ${
                                    history.basic_salary >
                                    structureHistory[index + 1]?.basic_salary
                                      ? "bg-green-100 text-green-800"
                                      : history.basic_salary <
                                          structureHistory[index + 1]
                                            ?.basic_salary
                                        ? "bg-red-100 text-red-800"
                                        : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {history.basic_salary >
                                  structureHistory[index + 1]?.basic_salary
                                    ? "↗ Increase"
                                    : history.basic_salary <
                                        structureHistory[index + 1]
                                          ?.basic_salary
                                      ? "↘ Decrease"
                                      : "→ No Change"}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowHistoryModal(false)}
                    className="px-7 py-2 text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Information Cards */}
        {/* <div className="grid grid-cols-1 gap-2">
          <div className="bg-white rounded-md shadow-md p-3">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Salary Queries</p>
                <p className="text-sm text-gray-600">hr@company.com</p>
                <p className="text-sm text-gray-600">+91 12345 67890</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Payroll Support</p>
                <p className="text-sm text-gray-600">payroll@company.com</p>
                <p className="text-sm text-gray-600">Ext: 2345</p>
              </div>
              <div className="pt-3 border-t border-gray-200">
                <button className="text-blue-600 text-sm font-medium hover:text-blue-800 flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  View Salary Policy
                </button>
              </div>
            </div>
          </div>
        </div> */}

        <div className="w-full bg-white dark:bg-gray-900 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 p-6 transition-all hover:shadow-lg">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            {/* Left - Heading and Info */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                {/* <HelpCircle className="h-5 w-5 text-blue-600" /> */}
                Need Help?
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Salary Queries */}
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1">
                    {/* <Mail className="h-4 w-4 text-blue-500" /> */}
                    Salary Queries
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    hr@company.com
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    {/* <Phone className="h-4 w-4 text-green-500" /> */}
                    +91 12345 67890
                  </p>
                </div>

                {/* Payroll Support */}
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1">
                    {/* <Mail className="h-4 w-4 text-blue-500" /> */}
                    Payroll Support
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    payroll@company.com
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    {/* <Phone className="h-4 w-4 text-green-500" /> */}
                    Ext: 2345
                  </p>
                </div>
              </div>
            </div>

            {/* Right - Button */}
            <div className="flex justify-center items-center">
              <button className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg flex items-center gap-2 shadow-sm transition-all">
                {/* <FileText className="h-4 w-4" /> */}
                View Salary Policy
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MySalaryStructure;
